import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { get } from '../../nucleo/api';
import { chavesDoRotuloDaAcao } from './arvore-acesso';
import type { Lista, Papel, Permissao } from './tipos';

export function usePapeis(habilitado = true) {
  return useQuery({ queryKey: ['/admin/papeis'], queryFn: () => get<Lista<Papel>>('/admin/papeis'), enabled: habilitado });
}

export function usePermissoes() {
  return useQuery({ queryKey: ['/admin/permissoes'], queryFn: () => get<Lista<Permissao>>('/admin/permissoes') });
}

/** Matriz de permissões por módulo instalado (N5): uma linha por módulo, uma caixa por ação. */
export function MatrizPermissoes({
  permissoes,
  marcadas,
  aoMudar,
  somenteLeitura,
}: {
  permissoes: Permissao[];
  marcadas: ReadonlySet<string>;
  aoMudar?: (proximas: Set<string>) => void;
  somenteLeitura?: boolean;
}) {
  const { t } = useTranslation('admin');
  const grupos = useMemo(() => {
    const m = new Map<string, { nome: string; itens: Permissao[] }>();
    for (const p of permissoes) {
      // Fase 2, W2a: permissão do núcleo (`nucleo.consolidado`) num grupo próprio, com o nome no idioma.
      const g = m.get(p.modulo) ?? { nome: p.modulo === 'nucleo' ? t('usuarios.papel.nucleo') : p.modulo_nome, itens: [] };
      g.itens.push(p);
      m.set(p.modulo, g);
    }
    return [...m.entries()];
  }, [permissoes, t]);

  if (!grupos.length) return <p className="text-sm text-tinta-3">{t('usuarios.papel.semModulos')}</p>;

  const alternar = (codigo: string) => {
    const n = new Set(marcadas);
    if (n.has(codigo)) n.delete(codigo);
    else n.add(codigo);
    aoMudar?.(n);
  };
  const alternarModulo = (itens: Permissao[], marcar: boolean) => {
    const n = new Set(marcadas);
    for (const i of itens) {
      if (marcar) n.add(i.codigo);
      else n.delete(i.codigo);
    }
    aoMudar?.(n);
  };

  return (
    <div className="flex flex-col gap-3">
      {grupos.map(([slug, g]) => {
        const todas = g.itens.every((i) => marcadas.has(i.codigo));
        return (
          <fieldset key={slug} className="min-w-0 rounded-lg border border-borda p-3">
            <legend className="px-1 text-sm font-semibold">{g.nome}</legend>
            {!somenteLeitura && (
              <label className="mb-2 flex cursor-pointer items-center gap-2 text-xs text-tinta-3">
                <input type="checkbox" checked={todas} onChange={() => alternarModulo(g.itens, !todas)} />
                {t('usuarios.papel.marcarTodas')}
              </label>
            )}
            <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 sm:grid-cols-3">
              {g.itens.map((i) => (
                <label key={i.codigo} className={`flex min-w-0 items-center gap-2 text-sm ${somenteLeitura ? 'text-tinta-2' : 'cursor-pointer'}`}>
                  <input type="checkbox" checked={marcadas.has(i.codigo)} disabled={somenteLeitura} onChange={() => alternar(i.codigo)} />
                  <span className="truncate" title={i.codigo}>
                    {t(chavesDoRotuloDaAcao(i.codigo, i.acao), { defaultValue: i.acao })}
                  </span>
                </label>
              ))}
            </div>
          </fieldset>
        );
      })}
    </div>
  );
}
